/**
 * La grilla del patron de desbloqueo.
 *
 * Es la misma grilla de nueve puntos que esta impresa en el talon de papel, y
 * se usa como en el telefono: se apoya el dedo (o el mouse) en el primer punto
 * y se arrastra por los demas. La linea que une los puntos se dibuja mientras
 * se pasa, y cada punto muestra su numero de orden, porque un patron dibujado
 * sin numeros no se puede leer despues (¿empezo arriba o abajo?), y el tecnico
 * lo necesita leer.
 *
 * Tambien se puede tocar de a un punto, para quien no puede arrastrar o usa el
 * teclado: cada punto es un boton. Tocar el ultimo lo deshace.
 *
 * Las reglas (que punto entra, cual se ignora, cual se agrega solo porque el
 * trazo le paso por arriba) viven en `tocarPunto()` y `arrastrarHasta()` en
 * `logica.ts`, y se prueban aparte. Aca solo esta la geometria.
 */
import { useRef, useState, type PointerEvent as PointerEventReact } from "react";
import { Eraser } from "lucide-react";

import { Codigo } from "@/components/ui/codigo";
import { cn } from "@/lib/utils";

import { PATRON_MAXIMO, PATRON_MINIMO, arrastrarHasta, patronATexto, tocarPunto } from "./logica";

const PUNTOS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/** Centro de cada punto, en porcentaje del ancho/alto de la grilla. */
function centro(punto: number): { x: number; y: number } {
  const fila = Math.floor((punto - 1) / 3);
  const col = (punto - 1) % 3;
  return { x: ((col * 2 + 1) / 6) * 100, y: ((fila * 2 + 1) / 6) * 100 };
}

/**
 * Que punto hay debajo de (x, y), dentro de la grilla. Solo cuenta si el dedo
 * esta cerca del centro del punto: pasar por el borde de una celda camino a
 * otra no la marca. Es lo que evita que el 1→3 recto marque el 2 por rozarlo
 * (eso lo decide `arrastrarHasta`, a proposito, no el roce).
 */
function puntoBajo(x: number, y: number, rect: DOMRect): number | null {
  const celda = rect.width / 3;
  const col = Math.floor((x - rect.left) / celda);
  const fila = Math.floor((y - rect.top) / celda);
  if (col < 0 || col > 2 || fila < 0 || fila > 2) return null;
  const cx = rect.left + (col + 0.5) * celda;
  const cy = rect.top + (fila + 0.5) * celda;
  const radio = celda * 0.38;
  if (Math.hypot(x - cx, y - cy) > radio) return null;
  return fila * 3 + col + 1;
}

export interface PatronDesbloqueoProps {
  valor: readonly number[];
  onCambiar: (puntos: number[]) => void;
  /** Solo se mira: sin botones, para mostrar un patron guardado. */
  soloLectura?: boolean;
  idAyuda?: string;
}

export function PatronDesbloqueo({
  valor,
  onCambiar,
  soloLectura = false,
  idAyuda,
}: PatronDesbloqueoProps): JSX.Element {
  const grilla = useRef<HTMLDivElement>(null);
  // Donde apoyo el dedo. Mientras no se mueva a otro punto es un toque, no un
  // trazo: recien al llegar al segundo punto el patron anterior se reemplaza.
  const inicio = useRef<number | null>(null);
  const trazo = useRef<number[]>([]);
  const [dibujando, setDibujando] = useState(false);
  const [dedo, setDedo] = useState<{ x: number; y: number } | null>(null);
  // Cuando se suelta un puntero, el navegador dispara ademas un `click` sobre
  // el boton. Ese click ya esta resuelto aca; se ignora si llega enseguida.
  const resueltoEn = useRef(0);

  const orden = new Map(valor.map((p, i) => [p, i + 1]));
  const ultimo = valor[valor.length - 1];

  function posicion(e: PointerEventReact<HTMLDivElement>, rect: DOMRect): { x: number; y: number } {
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  }

  function empezar(e: PointerEventReact<HTMLDivElement>): void {
    if (soloLectura || e.button !== 0) return;
    const rect = grilla.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const p = puntoBajo(e.clientX, e.clientY, rect);
    if (p === null) return;
    e.preventDefault();
    grilla.current?.setPointerCapture(e.pointerId);
    inicio.current = p;
    trazo.current = [];
    setDedo(posicion(e, rect));
  }

  function mover(e: PointerEventReact<HTMLDivElement>): void {
    if (inicio.current === null) return;
    const rect = grilla.current?.getBoundingClientRect();
    if (!rect) return;
    setDedo(posicion(e, rect));
    const p = puntoBajo(e.clientX, e.clientY, rect);
    if (p === null) return;

    if (trazo.current.length === 0) {
      if (p === inicio.current) return;
      // Recien aca es un arrastre: arranca de cero desde el punto de apoyo.
      trazo.current = arrastrarHasta([inicio.current], p);
      setDibujando(true);
      onCambiar(trazo.current);
      return;
    }
    const siguiente = arrastrarHasta(trazo.current, p);
    if (siguiente.length !== trazo.current.length) {
      trazo.current = siguiente;
      onCambiar(siguiente);
    }
  }

  function soltar(e: PointerEventReact<HTMLDivElement>): void {
    if (inicio.current === null) return;
    grilla.current?.releasePointerCapture(e.pointerId);
    if (trazo.current.length === 0) {
      // No se movio: fue un toque. Vale la regla del toque (agrega o deshace).
      onCambiar(tocarPunto(valor, inicio.current));
    }
    resueltoEn.current = Date.now();
    inicio.current = null;
    trazo.current = [];
    setDibujando(false);
    setDedo(null);
  }

  function tocar(p: number): void {
    // El click que sigue a un puntero ya se resolvio en `soltar`. El que llega
    // solo (teclado: Enter o espacio sobre el boton) si se atiende.
    if (Date.now() - resueltoEn.current < 400) return;
    onCambiar(tocarPunto(valor, p));
  }

  const segmentos = valor.slice(1).map((p, i) => {
    const a = centro(valor[i] as number);
    const b = centro(p);
    return { clave: `${valor[i]}-${p}`, a, b };
  });
  const puntaLibre =
    dibujando && dedo && ultimo !== undefined ? { a: centro(ultimo), b: dedo } : null;

  return (
    <div className="flex items-start gap-4">
      <div
        ref={grilla}
        role="group"
        aria-label="Patrón de desbloqueo"
        aria-describedby={idAyuda}
        onPointerDown={empezar}
        onPointerMove={mover}
        onPointerUp={soltar}
        onPointerCancel={soltar}
        className={cn(
          "relative grid h-44 w-44 shrink-0 select-none grid-cols-3 rounded-md border border-border bg-background",
          !soloLectura && "touch-none",
          dibujando && "cursor-grabbing",
        )}
      >
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {segmentos.map((s) => (
            <line
              key={s.clave}
              x1={s.a.x}
              y1={s.a.y}
              x2={s.b.x}
              y2={s.b.y}
              vectorEffect="non-scaling-stroke"
              className="stroke-primary"
              strokeWidth={3}
              strokeLinecap="round"
            />
          ))}
          {puntaLibre ? (
            <line
              x1={puntaLibre.a.x}
              y1={puntaLibre.a.y}
              x2={puntaLibre.b.x}
              y2={puntaLibre.b.y}
              vectorEffect="non-scaling-stroke"
              className="stroke-primary/40"
              strokeWidth={3}
              strokeLinecap="round"
              strokeDasharray="4 4"
            />
          ) : null}
        </svg>

        {PUNTOS.map((p) => {
          const n = orden.get(p);
          const marcado = n !== undefined;
          return (
            <div key={p} className="flex items-center justify-center">
              <button
                type="button"
                disabled={soloLectura}
                aria-pressed={marcado}
                aria-label={marcado ? `Punto ${p}, paso ${n}` : `Punto ${p}`}
                onClick={() => tocar(p)}
                className={cn(
                  "relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default",
                  marcado
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-transparent hover:border-foreground/50",
                  marcado && p === ultimo && !soloLectura && "ring-2 ring-primary/40 ring-offset-2",
                )}
              >
                {marcado ? n : "·"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="min-w-0 flex-1 space-y-2 text-sm">
        {valor.length === 0 ? (
          <p className="text-muted-foreground">
            {soloLectura
              ? "Sin patrón."
              : "Dibujalo como en el teléfono: apoyá y arrastrá por los puntos."}
          </p>
        ) : (
          <p>
            <span className="text-muted-foreground">Patrón: </span>
            <Codigo>{patronATexto(valor)}</Codigo>
            {!soloLectura && valor.length < PATRON_MINIMO ? (
              <span className="block text-xs text-muted-foreground">
                Faltan {PATRON_MINIMO - valor.length} para que sea un patrón válido.
              </span>
            ) : null}
            {!soloLectura && valor.length >= PATRON_MAXIMO ? (
              <span className="block text-xs text-muted-foreground">Ya están los nueve.</span>
            ) : null}
          </p>
        )}

        {!soloLectura && valor.length > 0 ? (
          <button
            type="button"
            onClick={() => onCambiar([])}
            className="inline-flex h-9 items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Eraser className="h-3.5 w-3.5" aria-hidden="true" />
            Borrar y dibujar de nuevo
          </button>
        ) : null}
      </div>
    </div>
  );
}
